import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';
import { useFragment } from 'react-relay/hooks';

import { Theme } from '@material-ui/core';
import { createStyles, withStyles, WithStyles } from '@material-ui/core/styles';
import { graphql } from 'babel-plugin-relay/macro';

import { Button, DialogChatActivityClose, Toolbar, ToolbarProps } from 'components/v2';

import { ActionsChatActivity_ChatActivity$key } from './__generated__/ActionsChatActivity_ChatActivity.graphql';
import ButtonChatActivityOpen from './ButtonChatActivityOpen';
import ButtonChatActivityReassign from './ButtonChatActivityReassign';

const styles = ({
  palette: {
    common: { white },
  },
  spacing: { unit },
}: Theme) =>
  createStyles({
    root: {
      backgroundColor: white,
      padding: unit,
    },
    button: {
      margin: `0px ${unit}px`,
    },
  });

const messages = defineMessages({
  actionClose: {
    id: 'deprecated.fragments.ActionsChatActivity.actionClose',
    defaultMessage: 'Mark as closed',
  },
});

export interface ActionsChatActivityProps extends Omit<ToolbarProps, 'classes' | 'variant'>, WithStyles<typeof styles> {
  fragmentRef: ActionsChatActivity_ChatActivity$key;
  variant: 'regular' | 'popper';
  onChatActivityAction?: (action: 'open' | 'close' | 'reassign', response: any) => void;
}

const ActionsChatActivity: React.FC<ActionsChatActivityProps> = ({
  classes,
  fragmentRef,
  variant,
  onChatActivityAction = () => {},
  ...rest
}) => {
  const chatActivity = useFragment<ActionsChatActivity_ChatActivity$key>(
    graphql`
      fragment ActionsChatActivity_ChatActivity on ChatActivity {
        _id
        status
        ...ButtonChatActivityOpen_ChatActivity
        ...ButtonChatActivityReassign_ChatActivity
      }
    `,
    fragmentRef,
  );

  const { formatMessage } = useIntl();
  const [open, setOpen] = useState(false);

  return (
    <>
      <Toolbar className={classes.root} {...rest}>
        {chatActivity.status === 'closed' ? (
          <ButtonChatActivityOpen
            fragmentRef={chatActivity}
            className={classes.button}
            variant="outlined"
            mutationConfig={{ callbackSuccess: (response) => onChatActivityAction('open', response) }}
            fullWidth
          />
        ) : (
          <>
            <Button className={classes.button} variant="outlined" fullWidth onClick={() => setOpen(true)}>
              {formatMessage(messages.actionClose)}
            </Button>
            <ButtonChatActivityReassign
              fragmentRef={chatActivity}
              className={classes.button}
              variant="outlined"
              mutationConfig={{ callbackSuccess: (response) => onChatActivityAction('reassign', response) }}
              fullWidth
            />
          </>
        )}
      </Toolbar>
      <DialogChatActivityClose
        ids={chatActivity ? [chatActivity._id] : []}
        open={open}
        mutationConfig={{ callbackSuccess: (response: any) => onChatActivityAction('close', response) }}
        onClose={() => setOpen(false)}
      />
    </>
  );
};

export default withStyles(styles)(ActionsChatActivity);
